import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Drawer } from 'antd';
import { DownloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    });

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', () => {});
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
    setShowDrawer(false);
  };

  const handleRemindLater = () => {
    setShowInstallPrompt(false);
    setShowDrawer(false);
    // Show the prompt again after 24 hours
    setTimeout(() => {
      setShowInstallPrompt(true);
    }, 24 * 60 * 60 * 1000);
  };

  if (isInstalled) return null;

  return (
    <>
      {showInstallPrompt && (
        <div className="pwa-install-button">
          <span className="pwa-install-pulse-ring" aria-hidden="true" />
          <Tooltip title="Install Maveshibids for faster, app-like access">
            <button
              className="pwa-install-btn-pill"
              onClick={() => setShowDrawer(true)}
            >
              <span className="pwa-install-icon-wrap">
                <DownloadOutlined />
              </span>
              <span className="pwa-install-label">Download App</span>
            </button>
          </Tooltip>
        </div>
      )}

      <Drawer
        title="Install Maveshibids App"
        placement="right"
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
        className="pwa-install-drawer"
        bodyStyle={{
          padding: 0,
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          backgroundImage: 'url(/assets/images/splash.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Content Layer */}
        <div 
          className="pwa-install-content"
          style={{
            padding: '24px',
            background: 'rgba(255, 255, 255, 0.4)',
            minHeight: 'calc(100vh - 55px)',
            position: 'relative',
            zIndex: 1
          }}
        >
          <div style={{
            padding: '20px',
            borderRadius: '12px',
            // backdropFilter: 'blur(4px)'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              marginBottom: '20px', 
              fontWeight: 700,
              color: '#ffffff',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 0.8)',
              lineHeight: '1.3'
            }}>
              Pakistan's First Realtime Auction Platform
            </h2>
            <p style={{ 
              fontSize: '16px', 
              marginBottom: '16px', 
              fontWeight: 600,
              color: '#ffffff',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 0.8)'
            }}>
              Install Maveshibids on your device for a better experience:
            </p>
            <ul style={{ 
              fontSize: '15px', 
              lineHeight: '1.8', 
              marginBottom: '24px',
              color: '#ffffff',
              fontWeight: 500,
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 0.8)'
            }}>
              <li>Quick access from your home screen</li>
              <li>Works offline</li>
              <li>Faster loading times</li>
              <li>Better performance</li>
            </ul>
          </div>
          <div className="pwa-install-actions" style={{ display: 'flex', gap: '12px' }}>
            <Button 
              onClick={handleRemindLater}
              icon={<ClockCircleOutlined />}
              style={{ flex: 1 }}
            >
              Remind me later
            </Button>
            <Button 
              type="primary" 
              onClick={handleInstall}
              icon={<DownloadOutlined />}
              style={{ flex: 1 }}
            >
              Install App
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default PWAInstallPrompt; 